import type { AuthSession } from '@aws-amplify/auth';
import * as Sentry from '@sentry/vue';
import { fetchAuthSession } from 'aws-amplify/auth';
import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig
} from 'axios';
import axios from 'axios';

import { useSnackbar } from '@/composables/useSnackbar';
import { logAppError } from '@/config/sentryConfig';
import { i18n } from '@/i18n';
import router from '@/router';
import { wasIntendedRouteRestored } from '@/router/intendedRoute';
import { logout, LogoutReason } from '@/services/auth/logoutService';
import { refreshAuthSession } from '@/services/auth/tokenRefresh';
import { OWN_PROFILE_PATH } from '@/services/userProfile/constants';
import useAuthStore from '@/stores/auth';

// Create snackbar instance once at module scope to avoid repeated allocations
// in the response interceptor (hot path).
const { showError } = useSnackbar();

/**
 * Safely get an abort signal, checking for null and abort status.
 * Returns null if the controller is null or the signal is already aborted.
 * This prevents race conditions where abort() is called during mid-flight requests.
 */
const getSafeAbortSignal = (
  controller: AbortController | null
): AbortSignal | null => {
  if (!controller) return null;
  const signal = controller.signal;
  return signal.aborted ? null : signal;
};

// Extend Axios config to include _retry flag
interface RetryAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const apiClient: AxiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json'
  }
});

// Shared controller used to abort all in-flight requests before logout.
// Replaced with a fresh instance after each cancellation so new requests
// made post-login are not born already aborted.
let pendingRequestsController = new AbortController();

// Gate that blocks any NEW requests from being dispatched once logout starts.
// Prevents poll timers that fire during the logout window from reaching Bobby
// with a dying session (which causes a 302→Cognito CORS error).
// Reset by resetSigningOut() at the start of the next sign-in.
let isSigningOut = false;

export const cancelPendingRequests = () => {
  isSigningOut = true;
  pendingRequestsController.abort();
  pendingRequestsController = new AbortController();
};

export const resetSigningOut = () => {
  isSigningOut = false;
};

// Guard that suppresses the response interceptor's auth-recovery and forced
// logout for own-profile auth failures while MFA setup is completing. Right
// after MFA setup the backend briefly returns 403 on /users/me until the
// freshly-updated JWT propagates; completeMFASetup() retries those itself, so
// the interceptor must pass them through rather than refreshing or logging out.
let isMfaSetupInProgress = false;

export const setMfaSetupInProgress = (value: boolean): void => {
  isMfaSetupInProgress = value;
};

/**
 * Check if logout is currently in progress.
 * Used by other parts of the app (e.g., SSE) to avoid operations during logout.
 */
export const isLogoutInProgress = (): boolean => isSigningOut;

export { getSafeAbortSignal };

// Request interceptor for adding the auth token
apiClient.interceptors.request.use(
  async (
    config: InternalAxiosRequestConfig
  ): Promise<InternalAxiosRequestConfig> => {
    // Block any new requests once logout has started — prevents poll timers
    // that fire during the logout window from reaching Bobby with an expiring
    // session and receiving a 302→Cognito redirect (which causes a CORS error).
    if (isSigningOut) {
      const cancelError = new axios.CanceledError('Signing out');
      (cancelError as any).isSessionExpired = true;
      return Promise.reject(cancelError);
    }

    // Attach the logout-cancellation signal when the caller hasn't supplied one.
    // Use safe getter to prevent null/aborted signal issues during logout.
    if (!config.signal) {
      const safeSignal = getSafeAbortSignal(pendingRequestsController);
      if (safeSignal) {
        config.signal = safeSignal;
      }
    }

    if (config.signal?.aborted) {
      return Promise.reject(
        new axios.CanceledError('Request aborted before auth')
      );
    }

    try {
      // Get the current authenticated user session
      const session: AuthSession = await fetchAuthSession();
      // Extract JWT token (id or access token depending on your setup)
      const token: string | undefined = session.tokens?.idToken?.toString();

      if (token) {
        // Add token to headers
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      // User is not authenticated or session expired
      // Log at info level since this is expected for unauthenticated users
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Auth check failed in interceptor, proceeding without token',
        level: 'info',
        data: { error: String(error) }
      });

      // If the error was a cancellation, propagate that specific error
      if (config.signal?.aborted) {
        return Promise.reject(new axios.CanceledError('Auth aborted'));
      }
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

/**
 * Perform the session-expired logout + redirect, but only when it should
 * interrupt the user: on a guarded route, or for a user with an active
 * session. Unauthenticated users on unguarded routes must not be bounced to
 * login by a background call failing for lack of a session.
 */
const handleSessionExpiry = async (section: string) => {
  const currentRoute = router.currentRoute.value;
  const isUnguarded = currentRoute.meta?.unguarded === true;
  const isAuthenticated = useAuthStore().isUserAuthenticated;

  // Authenticated users are always logged out, even on unguarded routes.
  // Unauthenticated users on guarded routes are also caught by the first condition.
  if (!isUnguarded || isAuthenticated) {
    // If this route was just restored from a saved intended route and is now
    // failing auth, don't hand it back to logout() to re-save — that traps the
    // user in a login → restore → fail → logout loop. Send them to the default
    // landing on re-login instead.
    const routeToRestore = wasIntendedRouteRestored(currentRoute)
      ? undefined
      : currentRoute;
    try {
      // Pass the route so the user returns to this page after re-login.
      await logout(undefined, LogoutReason.SessionExpired, routeToRestore);
    } catch (logoutError) {
      logAppError({
        error: logoutError,
        functionName: 'api.interceptor.response',
        section
      });
    }
  }
};

let inflightSessionExpiry: Promise<void> | null = null;

/**
 * Runs session-expiry handling as a single-flight operation.
 * If multiple failures trigger expiry at once, callers await the same in-flight
 * logout/redirect flow to avoid duplicate cleanup and navigation races.
 */
const runSessionExpiryOnce = async (section: string): Promise<void> => {
  if (inflightSessionExpiry) {
    await inflightSessionExpiry;
    return;
  }

  inflightSessionExpiry = handleSessionExpiry(section).finally(() => {
    inflightSessionExpiry = null;
  });

  await inflightSessionExpiry;
};

/**
 * Returns true for an auth failure that has a recovery path (silent token
 * refresh + one retry): a 401 on any endpoint. Bobby returns a clean 401 for an
 * expired/invalid token on every endpoint, /users/me included (confirmed by the
 * Bobby team under SESC-13206 — the earlier "403 means invalid token" wording in
 * their docs was a documentation error and has been removed). See docs/gotchas.md.
 */
const isRefreshableAuthError = (error: AxiosError): boolean =>
  error.response?.status === 401;

/**
 * Returns true when the own-profile request failed with an unrecoverable 4xx —
 * one that is neither 401 (refreshable, see isRefreshableAuthError) nor 403
 * (a permission denial, see isPermissionDeniedError). Other 4xx — e.g. a 409
 * session conflict after an SSO cookie change — are unrecoverable and should
 * trigger a forced logout. 5xx errors are excluded: a server outage is not a
 * session conflict and should surface as an initialization_error instead.
 */
const isProfileEndpointError = (error: AxiosError): boolean =>
  !!error.response &&
  error.response.status >= 400 &&
  error.response.status < 500 &&
  error.response.status !== 401 &&
  error.response.status !== 403 &&
  error.config?.url === OWN_PROFILE_PATH;

/**
 * Returns true for a genuine permission denial: a 403 on any endpoint. The user
 * is authenticated but not allowed to access the resource — unrecoverable, so it
 * is surfaced to the user as a "no access" message rather than failing silently
 * (and never triggers a refresh or logout).
 */
const isPermissionDeniedError = (error: AxiosError): boolean =>
  error.response?.status === 403;

// Response interceptor auth flow:
// 401 on any endpoint (Bobby's invalid-token response everywhere, /users/me
// included — SESC-13206): refresh once, then retry once.
//
// If recovery fails (dead session, refresh failure, or the retry still returns
// an auth error), handleSessionExpiry() logs out and redirects with
// session_expired. Unrecoverable paths set `isSessionExpired = true` so main.js
// preserves that reason during app initialization.
//
// If logout is already in progress, skip refresh and reject immediately.
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError): Promise<unknown> => {
    const originalRequest = error.config as RetryAxiosRequestConfig;

    // During MFA setup the own-profile endpoint briefly returns 403 until the
    // freshly-updated JWT propagates on the backend. completeMFASetup() retries
    // those itself, so pass profile auth failures through untouched here —
    // refreshing or forcing a logout would short-circuit that retry loop.
    if (isMfaSetupInProgress && originalRequest?.url === OWN_PROFILE_PATH) {
      return Promise.reject(error);
    }

    if (isRefreshableAuthError(error) && !originalRequest._retry) {
      originalRequest._retry = true; // Mark this request as already retried

      try {
        // If logout is already in progress, don't attempt token refresh.
        // This prevents trying to refresh with a session that's being torn down.
        if (isSigningOut) {
          const cancelError = new axios.CanceledError(
            'Logout in progress, aborting refresh'
          );
          (cancelError as any).isSessionExpired = true;
          return Promise.reject(cancelError);
        }

        // Try to refresh the session
        const session: AuthSession = await refreshAuthSession();

        // Get the new token
        const newToken: string | undefined =
          session.tokens?.idToken?.toString();

        if (newToken) {
          // Update the token in the failed request
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          // Retry the original request with the new token
          return apiClient(originalRequest);
        } else {
          throw new Error('No token available');
        }
      } catch (refreshError) {
        // Refresh failed → session is unrecoverable; log out and redirect.
        await runSessionExpiryOnce('session_expiry_refresh_failed');
        (refreshError as any).isSessionExpired = true;
        return Promise.reject(refreshError);
      }
    } else if (isRefreshableAuthError(error) && originalRequest._retry) {
      // Retry after refresh still returned a 401 — refresh didn't help, so the
      // session is unrecoverable; log out.
      Sentry.addBreadcrumb({
        category: 'auth',
        message:
          'Retry with refreshed token still returned an auth error, performing logout',
        level: 'warning'
      });

      await runSessionExpiryOnce('session_expiry_retry_still_failed');
      (error as any).isSessionExpired = true;
      return Promise.reject(error);
    }

    // /users/me returned a non-401 error (e.g. 409 session conflict after SSO
    // cookie change). The user is in a broken auth state — force logout.
    if (isProfileEndpointError(error)) {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: `Profile endpoint returned ${error.response?.status}, treating as session conflict`,
        level: 'warning'
      });

      if (!isSigningOut) {
        await runSessionExpiryOnce('session_expiry_profile_conflict');
      }
      (error as any).isSessionExpired = true;
    }

    // Genuine permission denial (authenticated but not allowed): surface a
    // "no access" message so the request doesn't fail silently. Skipped during
    // logout — a dying session shouldn't flash a permission error on the way out.
    if (isPermissionDeniedError(error) && !isSigningOut) {
      showError(i18n.global.t('generic.noPermissions'));
    }

    return Promise.reject(error);
  }
);

export default apiClient;
